
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { RefreshCw, UtensilsCrossed, Trash2, Search } from 'lucide-react';
import { Spinner } from '@/components/Spinner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface Recipe {
  id: string;
  title: string;
  imageUrl: string;
  calories: number;
  protein: number;
}

interface Meal {
  recipeId: string;
  portionSize: number;
}

interface MealTypeCardProps {
  mealType: string;
  mealLabel: { name: string; icon: string };
  suggestedCalories: number;
  dayName: string;
  selectedDate: Date;
  meals: Meal[];
  recipes: Recipe[];
  generatingMenu: string | null;
  isMobile: boolean;
  onGenerateMeal: (mealType: string) => void;
  onRemoveRecipe: (recipeId: string, day: string, mealType: string, index: number) => void;
  selectedDay: string;
}

const MealTypeCard: React.FC<MealTypeCardProps> = ({
  mealType,
  mealLabel,
  suggestedCalories,
  dayName,
  selectedDate,
  meals,
  recipes,
  generatingMenu,
  isMobile,
  onGenerateMeal,
  onRemoveRecipe,
  selectedDay
}) => {
  const navigate = useNavigate();

  const getRecipeById = (id: string) => {
    return recipes.find(recipe => recipe.id === id);
  };

  const handleGenerateMeal = () => {
    console.log(`🎯 MealTypeCard: User clicked generate for "${mealType}"`);
    console.log(`📋 Meal label: ${mealLabel.name}`);
    console.log(`🗓️ Day: ${selectedDay}`);
    
    // Debug específico para merienda
    if (mealType === 'afternoon_snack') {
      console.log('🔍 MERIENDA DEBUG - Button clicked!');
      console.log('🔍 Current meal type:', mealType);
      console.log('🔍 Current meals:', meals);
      console.log('🔍 Available recipes count:', recipes.length);
    }
    
    onGenerateMeal(mealType);
  };

  const handleExploreRecipes = () => {
    navigate('/recetas');
  };

  const isGenerating = generatingMenu === mealType;

  // Debug específico para renderizado de merienda
  if (mealType === 'afternoon_snack') {
    console.log('🎨 MERIENDA RENDER - Component rendering with:', {
      mealType,
      mealLabel: mealLabel.name,
      mealsCount: meals?.length || 0,
      isGenerating,
      generatingMenu
    });
  }

  return (
    <Card key={mealType} className="bg-white shadow-sm border-0">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-center">
          <CardTitle className="text-xl flex items-center">
            <span className="mr-2">{mealLabel.icon}</span>
            {mealLabel.name}
            <span className="ml-2 text-sm font-normal text-gray-500">
              (Sugerencia: {suggestedCalories} kcal)
            </span>
          </CardTitle>
          {/* Only show "Cambiar Receta" button when there are meals */}
          {meals?.length > 0 && (
            <Button 
              variant="outline" 
              size="sm"
              className="text-vegana-primary border-vegana-primary hover:bg-vegana-primary hover:text-white"
              onClick={handleGenerateMeal}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <Spinner size="sm" className="mr-2" />
              ) : (
                <RefreshCw size={16} className="mr-2" />
              )}
              {isGenerating ? 'Generando...' : 'Cambiar Receta'}
            </Button>
          )}
        </div>
        <CardDescription>
          {dayName} {format(selectedDate, 'd MMM', { locale: es })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {meals?.length ? (
          <div className="space-y-4">
            {meals.map((meal, index) => {
              const recipe = getRecipeById(meal.recipeId);
              if (!recipe) {
                console.warn(`Recipe not found for ID: ${meal.recipeId}`);
                return null;
              }

              return (
                <Card key={`${recipe.id}-${index}`} className="overflow-hidden border border-gray-200">
                  <div className="flex flex-col sm:flex-row">
                    <div className="w-full sm:w-24 h-24">
                      <img 
                        src={recipe.imageUrl} 
                        alt={recipe.title} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-grow p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <Link 
                            to={`/receta/${recipe.id}`} 
                            className="font-medium text-vegana-primary hover:text-vegana-accent"
                          >
                            {recipe.title}
                          </Link>
                          <div className="flex text-xs text-gray-500 gap-3 mt-1">
                            <span>{Math.round(recipe.calories * meal.portionSize)} kcal</span>
                            <span>{Math.round(recipe.protein * meal.portionSize)}g proteína</span>
                          </div>
                          <div className="text-sm mt-1 text-gray-700">
                            Porción: x{meal.portionSize}
                          </div>
                        </div>
                        <Button 
                          variant="ghost"
                          size="icon"
                          className="text-gray-400 hover:text-red-500 hover:bg-red-50"
                          onClick={() => onRemoveRecipe(recipe.id, selectedDay, mealType, index)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 text-gray-500">
            <UtensilsCrossed className="h-12 w-12 mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No hay recetas todavía</h3>
            <p className="text-sm text-center mb-4">
              Añade recetas a tu {mealLabel.name.toLowerCase()} del {dayName.toLowerCase()}
            </p>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex items-center gap-2 border-vegana-secondary text-vegana-secondary hover:bg-vegana-secondary hover:text-white"
                onClick={handleExploreRecipes}
              >
                <Search size={16} />
                Explorar Recetas
              </Button>
              <Button 
                variant="outline"
                size="sm"
                className="text-vegana-primary border-vegana-primary hover:bg-vegana-primary hover:text-white"
                onClick={handleGenerateMeal}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <RefreshCw size={16} className="mr-2" />
                )}
                Generar Receta
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MealTypeCard;
